'use client';

import type { FC } from 'react';
import { Avatar } from './Avatar';
import { UserAvatar } from '../UserAvatar';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { useDisplayUserName } from '../../hooks/useDisplayUserName';

interface UserInlineProps {
  userId: string | number;
  firstName: string | null;
  lastName?: string | null;
  level?: number;
  size?: 's' | 'm' | 'l';
  className?: string;
  onClick?: () => void;
}

const userInlineVariants = cva(
  'inline-flex items-center max-w-full transition-opacity duration-200 ease-smooth',
  {
    variants: {
      size: {
        s: 'gap-2',
        m: 'gap-2.5',
        l: 'gap-3',
      },
    },
    defaultVariants: {
      size: 's',
    },
  },
);

const nameVariants = cva(
  'text-white font-medium truncate min-w-0',
  {
    variants: {
      size: {
        s: 'text-sm',
        m: 'text-base',
        l: 'text-lg',
      },
    },
    defaultVariants: {
      size: 's',
    },
  },
);

export const UserInline: FC<UserInlineProps> = ({
  userId,
  firstName,
  lastName,
  level,
  size = 's',
  className,
  onClick,
}) => {
  const displayUserName = useDisplayUserName();
  const displayName = displayUserName(firstName, lastName);
  const avatarSrc = userId ? `/api/users/avatar/${userId}` : undefined;
  const fallback = displayName.charAt(0).toUpperCase();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const isClickable = !!onClick;

  return (
    <div
      className={cn(
        userInlineVariants({ size }),
        isClickable && 'cursor-pointer hover:opacity-80 active:opacity-60',
        className,
      )}
      onClick={isClickable ? handleClick : undefined}
    >
      {level !== undefined && userId ? (
        <UserAvatar userId={userId} level={level} size={size} />
      ) : (
        <Avatar src={avatarSrc} fallback={fallback} alt={displayName} size={size} />
      )}
      <span className={nameVariants({ size })}>
        {displayName}
      </span>
    </div>
  );
};
